/**
 * SkyTech Solutions — brand design tokens.
 *
 * Source of truth: doc/skytech-brand/ (logo SVGs + preview.html, Brand System v1.0).
 * Every value below is either lifted verbatim from that kit or derived from it and
 * marked as such. Frontends must consume these tokens rather than hard-coding hex.
 *
 * ── Accessibility note (SRS §8.3 requires WCAG 2.1 AA) ────────────────────────
 * Two brand colours do NOT reach the 4.5:1 ratio AA demands for normal-size text
 * on the Cloud White background (#F8F9FB):
 *
 *     Sky Blue    #4A90D9 -> 3.17:1   (AA large-text only, >=18.66px bold / 24px)
 *     Trust Green #2D8B5E -> 4.01:1   (AA large-text only)
 *
 * The kit's own preview.html uses Sky Blue for 11-12px captions, which would fail
 * an audit. Rather than alter the brand, we keep the original values for fills,
 * borders, icons and large display type, and add hue-preserving `*Ink` variants
 * that are the LIGHTEST shade of the same hue reaching 4.5:1. Use `*Ink` for any
 * body-size text.
 */
/** Raw brand colours, exactly as specified in the brand kit. */
export const palette = {
    /** Deep Navy — primary. 35% of surface area. 15.53:1 on cloud. */
    navy900: '#0F1F3D',
    /** Mid Navy — secondary. 20%. 12.72:1 on cloud. */
    navy700: '#1A2D5A',
    /** Gradient terminal stop from logo-full.svg. 8.06:1 on cloud. */
    navy600: '#2B4A8E',
    /** Sky Blue — accent. 10%. AA for LARGE text only on cloud (3.17:1). */
    sky500: '#4A90D9',
    /** Cloud White — background. 25%. */
    cloud: '#F8F9FB',
    /** Frost Gray — borders/dividers. 8%. Non-text use only. */
    frost: '#E8ECF1',
    /** Trust Green — success. 2%. AA for LARGE text only on cloud (4.01:1). */
    green600: '#2D8B5E',
    white: '#FFFFFF',
};
/**
 * Derived AA-safe text colours. Same hue and saturation as their brand parent,
 * darkened to the lightest shade that clears 4.5:1 on Cloud White.
 */
export const paletteInk = {
    /** Sky Blue for body text. 4.50:1 on cloud, 4.74:1 on white. */
    sky500Ink: '#2975C3',
    /** Trust Green for body text. 4.55:1 on cloud, 4.80:1 on white. */
    green600Ink: '#2A8158',
};
/**
 * Semantic states. Signed off per project decision (2026-07-24): the five roles are
 * success, error/danger, warning, info and neutral. Each is verified >=4.5:1 on
 * Cloud White so it is safe for normal-size status text.
 *
 *   Success  completed operations        (Trust Green, AA-darkened)
 *   Danger   validation errors, failures (red)
 *   Warning  attention required          (amber/orange)
 *   Info     neutral information         (blue)
 *   Neutral  disabled / secondary text   (slate grey)
 */
export const paletteSemantic = {
    /** 4.55:1 on cloud. The AA-safe Trust Green; use `green600` for fills/badges. */
    success: '#2A8158',
    /** 6.20:1 on cloud. */
    danger: '#B3261E',
    /** 5.63:1 on cloud. Amber-brown; lighter ambers cannot reach AA. */
    warning: '#8A5A00',
    /** 6.28:1 on cloud. */
    info: '#1A5F9E',
    /** 6.05:1 on cloud. Secondary text, disabled states, table meta. */
    neutral: '#556070',
};
/** Logo icon gradient (shield fill) from logo-full.svg. */
export const gradients = {
    shield: {
        stops: [
            { offset: '0%', color: palette.navy900 },
            { offset: '60%', color: palette.navy700 },
            { offset: '100%', color: palette.navy600 },
        ],
        css: `linear-gradient(135deg, ${palette.navy900} 0%, ${palette.navy700} 60%, ${palette.navy600} 100%)`,
    },
};
/**
 * Typography. Per project decision (2026-07-24), the application UI relies on
 * self-hosted Inter (vendored via @fontsource, served from our own origin so the
 * §10.5 `font-src 'self'` CSP holds — no Google Fonts CDN). Helvetica Neue is kept
 * only for brand/marketing assets where it is actually installed; it is NOT relied
 * on for the interface because it is absent on Windows and Android, most of the
 * Kenyan market. System fonts follow as fallbacks for consistent rendering.
 */
export const typography = {
    fontFamily: `'Inter Variable', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
    fontMono: `'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace`,
    /** Light 300 for display type is the defining trait of this brand. */
    scale: {
        h1: { size: '48px', weight: 300, tracking: '1px', leading: 1.1 },
        h2: { size: '32px', weight: 300, tracking: '0.5px', leading: 1.2 },
        h3: { size: '24px', weight: 400, tracking: '0', leading: 1.3 },
        body: { size: '16px', weight: 400, tracking: '0', leading: 1.5 },
        caption: { size: '12px', weight: 400, tracking: '1px', leading: 1.4 },
        /** Small all-caps labels — the kit's signature wide tracking. */
        eyebrow: { size: '11px', weight: 400, tracking: '3px', leading: 1.4 },
    },
};
export const radii = { sm: '4px', md: '6px', lg: '12px', full: '9999px' };
export const shadows = {
    card: '0 2px 12px rgba(15, 31, 61, 0.06)',
    hover: '0 4px 20px rgba(15, 31, 61, 0.15)',
};
/** Tailwind breakpoints mandated by SRS §4.9. */
export const breakpoints = { sm: '640px', md: '768px', lg: '1024px', xl: '1280px' };
/** SRS §4.9 — minimum touch target. */
export const a11y = { minTouchTargetPx: 44, contrastNormalText: 4.5, contrastLargeText: 3 };
export const brandCopy = {
    name: 'SkyTech Solutions',
    tagline: 'Your Security, Our Promise',
    eyebrow: 'CCTV · ROUTERS · ELECTRONICS · NAIROBI',
    locality: 'Nairobi CBD · Kenya',
    /** Single contact line for orders and support — display and dialable forms. */
    phone: '+254 705 588442',
    phoneE164: '+254705588442',
    partners: ['Hikvision', 'Dahua', 'TP-Link', 'Ubiquiti', 'MikroTik'],
};
/**
 * Emits the token set as CSS custom properties for the Tailwind v4 `@theme` block.
 * Keeps one source of truth across both frontends.
 */
export function toCssVariables() {
    return [
        `--color-navy-900: ${palette.navy900};`,
        `--color-navy-700: ${palette.navy700};`,
        `--color-navy-600: ${palette.navy600};`,
        `--color-sky-500: ${palette.sky500};`,
        `--color-sky-ink: ${paletteInk.sky500Ink};`,
        `--color-cloud: ${palette.cloud};`,
        `--color-frost: ${palette.frost};`,
        `--color-green-600: ${palette.green600};`,
        `--color-green-ink: ${paletteInk.green600Ink};`,
        `--color-success: ${paletteSemantic.success};`,
        `--color-danger: ${paletteSemantic.danger};`,
        `--color-warning: ${paletteSemantic.warning};`,
        `--color-info: ${paletteSemantic.info};`,
        `--color-neutral: ${paletteSemantic.neutral};`,
    ].join('\n  ');
}
//# sourceMappingURL=brand.js.map